import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { useState } from 'react';
import { isEqual } from 'lodash-es';
import { useOverlayContext } from 'seagull';
import { toAmendmentScope } from '~/components/ai/BulkAmendmentAgentPane/utils/amendmentScope';
import { useConversationBoardScope } from '~/components/ai/conversation/useConversationBoardScope';
import { pickAiSearchBoardState } from '~/hooks/reactQuery/createAiSearchStore';
import { useLocaleKeys } from '~/i18n';
import { relationshipSummaryModel } from '~/models';
import { useIoContext } from '~/providers/IoContext';
import { selectedRowsFrom } from '~/utils/selectedRows';
import { isUserDrivenNarrowing } from '../AgreementsAiSearch/hooks/agreementFilterActivity';
import { useAgreementsAiSearchStore } from '../AgreementsAiSearch/hooks/useAgreementsAiSearchStore';
import { deriveActiveFilterText, isAgreementSearchFilterShape, readAmendmentPaneOpenFromLocationState } from '../AgreementsDashboard.utils';
const NO_CHANGED_AGREEMENTS = new Set();
const restoredAgreementIds = (ids)=>ids == null ? null : ids.filter((id)=>typeof id === 'string' && id.length > 0);
export const useBulkAmendmentAgentPane = (param)=>{
    let { agreements, selected, setSelected, selectedAgreementIds, getSelectedAgreementIds, aiSearchScope, isVendor, refreshLanes, boardHandedBackToastTestId } = param;
    const { browser: { persistLocationState, readPersistedLocationState } } = useIoContext();
    const { t } = useLocaleKeys();
    const { showSuccessToast } = useOverlayContext();
    const boardScope = useConversationBoardScope((param)=>{
        let { filter } = param;
        return filter;
    });
    // eslint-disable-next-line no-restricted-syntax -- WHY: a collapsible pane, not a modal; open state is persisted to history state and restored on mount, same as the drafts pane
    const [isOpen, setIsOpen] = useState(()=>readAmendmentPaneOpenFromLocationState(readPersistedLocationState()));
    const [changedAgreementIds, setChangedAgreementIds] = useState(NO_CHANGED_AGREEMENTS);
    const [isRefreshingAfterSave, setIsRefreshingAfterSave] = useState(false);
    const setOpen = (open)=>{
        setIsOpen(open);
        persistLocationState({
            amendmentPaneOpen: open
        });
    };
    async function refreshAfterSave(agreementIds) {
        setIsRefreshingAfterSave(true);
        await refreshLanes();
        setIsRefreshingAfterSave(false);
        if (agreementIds.length > 0) {
            setChangedAgreementIds((current)=>new Set([
                    ...current,
                    ...agreementIds
                ]));
        }
    }
    function clearAgreementHighlight(agreementId) {
        setChangedAgreementIds((current)=>current.has(agreementId) ? new Set([
                ...current
            ].filter((id)=>id !== agreementId)) : current);
    }
    function readBoardScope() {
        // WHY read the selection at call time: the hand-back runs after BulkPaneShell awaits the clear, so a prop captured at render can miss rows the seller checked meanwhile.
        return {
            search: pickAiSearchBoardState(useAgreementsAiSearchStore.getState()),
            selectedIds: getSelectedAgreementIds()
        };
    }
    function handBackBoardScope() {
        const handedBack = boardScope.handBack(readBoardScope());
        // WHY abort first: a parse still in flight resolves into setFilter and would land on top of the board we just restored, exactly as clearFilter guards against (ANCR-5802).
        if (handedBack.search != null) {
            var _useAgreementsAiSearchStore_getState_abortInFlight, _useAgreementsAiSearchStore_getState;
            (_useAgreementsAiSearchStore_getState_abortInFlight = (_useAgreementsAiSearchStore_getState = useAgreementsAiSearchStore.getState()).abortInFlight) === null || _useAgreementsAiSearchStore_getState_abortInFlight === void 0 ? void 0 : _useAgreementsAiSearchStore_getState_abortInFlight.call(_useAgreementsAiSearchStore_getState);
            useAgreementsAiSearchStore.setState(_object_spread_props(_object_spread({}, handedBack.search), {
                abortInFlight: null,
                errorCode: null
            }));
        }
        if (handedBack.selectedIds != null) setSelected(selectedRowsFrom([
            ...handedBack.selectedIds
        ]));
        if (handedBack.search != null || handedBack.selectedIds != null) {
            showSuccessToast(t.ai.drafting.messages.boardHandedBack(), boardHandedBackToastTestId);
        }
    }
    // WHY: the chip row seeds a display-default filter on mount, so only a filter that is actually narrowing the table describes a scope the seller chose. A refused search narrows the table to nothing WITHOUT a filter, and its query text describes no applied scope.
    const narrowedByAppliedFilter = aiSearchScope.isNarrowingTable && aiSearchScope.filter != null;
    return {
        open: ()=>setOpen(true),
        changedAgreementIds,
        clearAgreementHighlight,
        isRefreshingAfterSave,
        paneProps: {
            scope: toAmendmentScope({
                currentFilter: narrowedByAppliedFilter ? aiSearchScope.filter : null,
                selectedAgreementIds,
                totalMatchCount: narrowedByAppliedFilter ? aiSearchScope.totalMatchCount : agreements.length,
                activeFilterText: narrowedByAppliedFilter ? deriveActiveFilterText(aiSearchScope.lastQuery, aiSearchScope.filterEdited) : ''
            }),
            agreementNames: agreements.filter((agreement)=>selected[agreement.id] === true).map((agreement)=>{
                var _relationshipSummaryModel_displayedTitle;
                return (_relationshipSummaryModel_displayedTitle = relationshipSummaryModel.displayedTitle(agreement, isVendor)) !== null && _relationshipSummaryModel_displayedTitle !== void 0 ? _relationshipSummaryModel_displayedTitle : '';
            }).filter((name)=>name.length > 0),
            // WHY: an empty query means the replayed turn carried no prompt text (a document upload); setFilter would leave the filter showing in the chips but never applied to the table, so editFilter marks it user-driven instead.
            onFilterUpdate: (filter, query)=>{
                const board = readBoardScope();
                if (query.length > 0) useAgreementsAiSearchStore.getState().setFilter(filter, query);
                else useAgreementsAiSearchStore.getState().editFilter(filter);
                boardScope.rememberStamp(board, readBoardScope());
            },
            onSelectionUpdate: (ids)=>{
                const board = readBoardScope();
                boardScope.rememberStamp(board, _object_spread_props(_object_spread({}, board), {
                    selectedIds: ids
                }));
                setSelected(selectedRowsFrom(ids));
            },
            onChatCleared: handBackBoardScope,
            onSellerPrompted: boardScope.forget,
            onScopeRestore: (restored)=>{
                // WHY: editFilter — not setFilter — because the results lane only runs for a user-driven filter, and setFilter's empty query would leave the restored scope showing in the chips but never applied to the table.
                // WHY: read the store at call time — this callback runs in the recent() .then, so a prop captured at render cannot see a narrowing the seller performed while the request was in flight.
                // WHY a replayed filter does not count: the replay overwrote the seller's narrowing with the thread's own, so its rows belong with it (ANCR-5785).
                const searchState = useAgreementsAiSearchStore.getState();
                const tableAlreadyNarrowed = !restored.filterReplayed && isUserDrivenNarrowing(searchState);
                const filterToRestore = !tableAlreadyNarrowed && isAgreementSearchFilterShape(restored.currentFilter) ? restored.currentFilter : null;
                // WHY: re-checking the old rows onto a table the seller narrowed to another set leaves every turn acting on rows they cannot see (ANCR-5785).
                const tableShowsAnotherSet = tableAlreadyNarrowed && !isEqual(searchState.filter, restored.currentFilter);
                const selectionToRestore = !tableShowsAnotherSet && getSelectedAgreementIds().length === 0 ? restoredAgreementIds(restored.selectedAgreementIds) : null;
                const board = readBoardScope();
                if (filterToRestore != null) {
                    useAgreementsAiSearchStore.getState().editFilter(filterToRestore);
                }
                if (selectionToRestore != null) {
                    setSelected(selectedRowsFrom(selectionToRestore));
                }
                if (filterToRestore != null || selectionToRestore != null) {
                    boardScope.rememberStamp(board, {
                        search: pickAiSearchBoardState(useAgreementsAiSearchStore.getState()),
                        selectedIds: selectionToRestore !== null && selectionToRestore !== void 0 ? selectionToRestore : board.selectedIds
                    });
                }
            },
            onAgreementsChanged: refreshAfterSave,
            isOpen,
            onOpenChange: setOpen
        }
    };
};
